import { useCallback, useMemo, useState } from 'react';
import { validateClaim } from '../api/claimsClient';
import { indexValidationErrors } from '../utils/indexValidationErrors';

/**
 * @typedef {{ field: string; section: string; code: string; message: string; severity?: string }} ValidationIssue
 * @typedef {{ valid: boolean; errors: ValidationIssue[]; warnings: ValidationIssue[] }} ValidationResult
 */

/**
 * @param {string|null} claimId
 */
export function useClaimValidation(claimId) {
  const [result, setResult] = useState(/** @type {ValidationResult|null} */ (null));
  const [validationError, setValidationError] = useState(/** @type {any} */ (null));
  const [validating, setValidating] = useState(false);

  const runValidation = useCallback(async () => {
    if (!claimId) {
      setValidationError(null);
      return null;
    }
    setValidating(true);
    setValidationError(null);
    try {
      const r = await validateClaim(claimId);
      setResult(r);
      return r;
    } catch (err) {
      setResult(null);
      setValidationError({
        detail: /** @type {any} */ (err)?.response?.data?.detail
          || /** @type {any} */ (err)?.message
          || 'Validation request failed',
        status: /** @type {any} */ (err)?.response?.status,
      });
      throw err;
    } finally {
      setValidating(false);
    }
  }, [claimId]);

  const clearFieldIssue = useCallback((/** @type {string} */ field) => {
    if (!field) return;
    setResult((prev) => {
      if (!prev) return prev;
      const matches = (/** @type {ValidationIssue} */ issue) => issue.field === field;
      const errors = (prev.errors || []).filter((issue) => !matches(issue));
      const warnings = (prev.warnings || []).filter((issue) => !matches(issue));
      if (errors.length === (prev.errors || []).length && warnings.length === (prev.warnings || []).length) {
        return prev;
      }
      return { ...prev, valid: errors.length === 0, errors, warnings };
    });
  }, []);

  const { errorsByField, errorsBySection } = useMemo(() => {
    if (!result) return { errorsByField: new Map(), errorsBySection: new Map() };
    const all = [...(result.errors || []), ...(result.warnings || [])];
    const indexed = indexValidationErrors(all);
    return { errorsByField: indexed.byField, errorsBySection: indexed.bySection };
  }, [result]);

  return { result, validationError, errorsByField, errorsBySection, validating, runValidation, clearFieldIssue };
}
